// components/ClinicRevenueOverview.tsx
// Owner-only page: clinic-wide revenue overview showing total per doctor + grand total.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Card, Row, Col, Statistic, Typography, DatePicker,
  Space, Button, Spin, Tag, Empty, Progress, Divider, Avatar, message,
  Drawer, Grid
} from 'antd';
import {
  ArrowLeftOutlined, ReloadOutlined, UserOutlined,
  DollarOutlined, TeamOutlined, BarChartOutlined, FireOutlined,
  PrinterOutlined, FilePdfOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import API from '../config/api';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { RevenueAggregate } from '../utils/revenue';
import { printHtml } from './printUtils';
import { useClinicContext } from './ClinicContext';
import './ClinicRevenueOverview.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

interface DoctorRevenue {
  doctorId: string;
  doctorName: string;
  net: RevenueAggregate | null;
  error?: boolean;
}

interface PatientRevenueRow {
  doctorId: string;
  doctorName: string;
  patientKey: string;
  patientName: string;
  visits: number;
  amountPaid: number;
}

// Animated count-up on load
const CountUp: React.FC<{ end: number; prefix?: string; suffix?: string }> = ({ end, prefix = '', suffix = '' }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (end === 0) { setValue(0); return; }
    const duration = 800;
    const steps = 30;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) { setValue(end); clearInterval(timer); }
      else { setValue(Math.round(current)); }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);
  return <span>{prefix}{value.toLocaleString()}{suffix}</span>;
};

const DOCTOR_COLORS = ['#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#faad14'];

const escapeHtml = (value: unknown): string => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatMoney = (value: number): string => `${Math.round(value || 0).toLocaleString()} EGP`;

const formatPeriodLabel = (range: [Dayjs | null, Dayjs | null], activeFilter: string): string => {
  const start = range[0]?.format('YYYY-MM-DD') || '';
  const end = range[1]?.format('YYYY-MM-DD') || '';
  const filterLabel = activeFilter === 'custom'
    ? 'Custom period'
    : `${activeFilter.charAt(0).toUpperCase()}${activeFilter.slice(1)}`;

  if (!start && !end) return filterLabel;
  if (start === end) return `${filterLabel}: ${start}`;
  return `${filterLabel}: ${start} to ${end}`;
};

interface ClinicRevenueReportInput {
  clinicName: string;
  periodLabel: string;
  generatedAt: string;
  rows: PatientRevenueRow[];
  grandPaid: number;
  grandBillings: number;
}

const buildClinicRevenueReportHtml = ({
  clinicName,
  periodLabel,
  generatedAt,
  rows,
  grandPaid,
  grandBillings,
}: ClinicRevenueReportInput): string => {
  const sortedRows = rows
    .slice()
    .sort((a, b) => {
      if (b.amountPaid !== a.amountPaid) return b.amountPaid - a.amountPaid;
      return a.patientName.localeCompare(b.patientName);
    });

  const tableRows = sortedRows.map((row, index) => {
    return `
      <tr>
        <td class="rank">${index + 1}</td>
        <td>${escapeHtml(row.doctorName)}</td>
        <td>${escapeHtml(row.patientName || 'Unknown patient')}</td>
        <td class="num">${Number(row.visits || 0).toLocaleString()}</td>
        <td class="num">${formatMoney(row.amountPaid)}</td>
      </tr>
    `;
  }).join('');

  const emptyRows = `
    <tr>
      <td colspan="5" class="empty">No revenue data found for this period.</td>
    </tr>
  `;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Clinic Revenue Report</title>
  <style>
    @page { size: A4 portrait; margin: 14mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: #111827;
      background: #ffffff;
      font-family: Arial, "Segoe UI", Tahoma, sans-serif;
      font-size: 12px;
      line-height: 1.45;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .report {
      width: 100%;
      max-width: 980px;
      margin: 0 auto;
      padding: 26px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      gap: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid #1677ff;
    }
    .title {
      margin: 0;
      font-size: 26px;
      line-height: 1.15;
      color: #0f172a;
    }
    .clinic {
      margin-top: 8px;
      font-size: 15px;
      font-weight: 700;
      color: #334155;
    }
    .meta {
      min-width: 210px;
      text-align: right;
      color: #475569;
    }
    .meta div { margin-bottom: 5px; }
    .summary {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      margin: 22px 0;
    }
    .metric {
      border: 1px solid #d9e2ec;
      border-top: 4px solid #1677ff;
      border-radius: 8px;
      padding: 12px;
      min-height: 74px;
    }
    .metric .label {
      color: #64748b;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: .04em;
    }
    .metric .value {
      margin-top: 6px;
      font-size: 19px;
      font-weight: 800;
      color: #0f172a;
    }
    .metric.visits { border-top-color: #7c3aed; }
    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #d9e2ec;
      overflow: hidden;
    }
    thead { background: #eff6ff; }
    th, td {
      padding: 9px 10px;
      border-bottom: 1px solid #e5e7eb;
      text-align: left;
      vertical-align: top;
    }
    th {
      color: #1e3a8a;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: .04em;
    }
    tbody tr:nth-child(even) { background: #f8fafc; }
    .rank { width: 42px; color: #64748b; font-weight: 700; }
    .num { text-align: right; white-space: nowrap; }
    .empty {
      padding: 28px;
      text-align: center;
      color: #64748b;
    }
    .footer {
      margin-top: 16px;
      color: #64748b;
      font-size: 11px;
      text-align: center;
    }
    @media print {
      .report { padding: 0; max-width: none; }
      .metric { break-inside: avoid; }
      tr { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <main class="report">
    <section class="header">
      <div>
        <h1 class="title">Clinic Revenue Report</h1>
        <div class="clinic">${escapeHtml(clinicName || 'Clinic')}</div>
      </div>
      <div class="meta">
        <div><strong>Period:</strong> ${escapeHtml(periodLabel)}</div>
        <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
      </div>
    </section>

    <section class="summary" aria-label="Revenue summary">
      <div class="metric">
        <div class="label">Revenue collected</div>
        <div class="value">${formatMoney(grandPaid)}</div>
      </div>
      <div class="metric visits">
        <div class="label">Visits</div>
        <div class="value">${Number(grandBillings || 0).toLocaleString()}</div>
      </div>
    </section>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Doctor</th>
          <th>Patient name</th>
          <th class="num">Visits</th>
          <th class="num">Paid amount</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows || emptyRows}
      </tbody>
    </table>

    <div class="footer">Generated from NoWaiting Dashboard revenue module.</div>
  </main>
</body>
</html>`;
};

const ClinicRevenueOverview: React.FC = () => {
  const navigate = useNavigate();
  const { selectedClinic } = useClinicContext();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [doctorRevenues, setDoctorRevenues] = useState<DoctorRevenue[]>([]);
  const [patientRevenueRows, setPatientRevenueRows] = useState<PatientRevenueRow[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('today');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('day'),
    dayjs().endOf('day'),
  ]);

  const clinicId = localStorage.getItem('clinicId') || '';

  // Today's walk-in count comes from Firestore waiting_list (not money — just a
  // headcount). Revenue numbers ALWAYS come from BE.
  const [fsTotalVisits, setFsTotalVisits] = useState<number | null>(null);

  useEffect(() => {
    if (activeFilter !== 'today' || !clinicId) { setFsTotalVisits(null); return; }
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    getDocs(collection(db, 'clinics', clinicId, 'waiting_list', today, 'patients'))
      .then(snap => setFsTotalVisits(snap.size))
      .catch(() => setFsTotalVisits(null));
  }, [activeFilter, clinicId]);

  // Real-time refresh: when BE writes the dailyRevenue mirror, refetch.
  useEffect(() => {
    if (!clinicId) return;
    const unsub = onSnapshot(
      collection(db, 'clinics', clinicId, 'dailyRevenue'),
      (snap) => { if (!snap.metadata.hasPendingWrites) fetchRevenueData(); },
      () => {},
    );
    return () => unsub();
    // fetchRevenueData captures dateRange via useCallback; that's the desired behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const fetchRevenueData = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      // 1. Resolve clinic→doctor mapping from Firestore (canonical source for the
      //    doctor list — the same path the rest of the app uses).
      const dcaSnap = await getDocs(
        query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
      );
      const doctorIds: string[] = Array.from(
        new Set(dcaSnap.docs.map(d => d.data().doctor_id as string).filter(Boolean))
      );
      if (doctorIds.length === 0) { setDoctorRevenues([]); setPatientRevenueRows([]); return; }

      // 2. Fetch doctor names in one whereIn round-trip
      const BATCH = 30;
      const nameMap = new Map<string, string>();
      for (let i = 0; i < doctorIds.length; i += BATCH) {
        const batch = doctorIds.slice(i, i + BATCH);
        const snap = await getDocs(
          query(collection(db, 'doctors'), where('doctor_id', 'in', batch))
        );
        snap.docs.forEach(d => nameMap.set(d.data().doctor_id, d.data().name || d.data().doctor_id));
      }

      // 3. Single network call to the new SST clinic endpoint. We pass doctor_ids
      //    so the backend uses the Firestore-resolved list (works even when older
      //    Billing rows have an empty clinic_id field).
      const params = {
        startDate: dateRange[0]?.startOf('day').format('YYYY-MM-DD'),
        endDate:   dateRange[1]?.endOf('day').format('YYYY-MM-DD'),
        doctor_ids: doctorIds.join(','),
      };

      const [res, ...doctorBreakdownResponses] = await Promise.all([
        axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.REVENUE_CLINIC(clinicId)}`,
          { params }
        ),
        ...doctorIds.map((doctorId) => axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.REVENUE_DOCTOR_BREAKDOWN(doctorId)}`,
          { params: { startDate: params.startDate, endDate: params.endDate } }
        )),
      ]);

      const breakdown: Array<RevenueAggregate & { doctor_id: string }> =
        res.data?.success ? (res.data.data?.breakdown || []) : [];
      const breakdownMap = new Map(breakdown.map(b => [b.doctor_id, b]));

      // 4. Merge with the full doctor list so doctors with zero billings still show
      const results: DoctorRevenue[] = doctorIds.map((doctorId) => {
        const doctorName = nameMap.get(doctorId) || doctorId;
        const agg = breakdownMap.get(doctorId);
        return {
          doctorId,
          doctorName,
          net: agg ? {
            totalRevenue:    agg.totalRevenue,
            amountCollected: agg.amountCollected,
            refundedAmount:  agg.refundedAmount,
            pendingAmount:   agg.pendingAmount,
            billingCount:    agg.visitCount ?? agg.billingCount,
            visitCount:      agg.visitCount ?? agg.billingCount,
            recordCount:     agg.recordCount,
          } : { totalRevenue: 0, amountCollected: 0, refundedAmount: 0, pendingAmount: 0, billingCount: 0 },
        };
      });
      setDoctorRevenues(results);

      const patientMap = new Map<string, PatientRevenueRow>();
      doctorBreakdownResponses.forEach((response, doctorIndex) => {
        const doctorId = doctorIds[doctorIndex];
        const doctorName = nameMap.get(doctorId) || doctorId;
        const items = response.data?.success ? (response.data.data?.items || []) : [];

        items.forEach((item: any) => {
          const paid = Number(item.amountPaid) || (item.paymentStatus === 'paid' ? Number(item.totalAmount) || 0 : 0);
          if (paid <= 0) return;

          const patientKey = item.patient_id || item.patient_name || 'unknown';
          const mapKey = `${doctorId}::${patientKey}`;
          const existing = patientMap.get(mapKey) || {
            doctorId,
            doctorName,
            patientKey,
            patientName: item.patient_name || 'Unknown patient',
            visits: 0,
            amountPaid: 0,
          };
          existing.visits += 1;
          existing.amountPaid += paid;
          patientMap.set(mapKey, existing);
        });
      });
      setPatientRevenueRows(Array.from(patientMap.values()));
    } catch (err) {
      console.error('Error fetching clinic revenue:', err);
      setPatientRevenueRows([]);
    } finally {
      setLoading(false);
    }
  }, [clinicId, dateRange]);

  useEffect(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  // ── Date filter shortcuts ────────────────────────────────────────
  const applyFilter = (filter: string) => {
    setActiveFilter(filter);
    const now = dayjs();
    const ranges: Record<string, [Dayjs, Dayjs]> = {
      today: [now.startOf('day'), now.endOf('day')],
      week: [now.startOf('week'), now.endOf('week')],
      month: [now.startOf('month'), now.endOf('month')],
      year: [now.startOf('year'), now.endOf('year')],
    };
    if (ranges[filter]) setDateRange(ranges[filter]);
  };

  // ── Grand totals — ALWAYS from BE (single source of truth) ──────────────────
  const grandTotal    = doctorRevenues.reduce((sum, d) => sum + (d.net?.totalRevenue    || 0), 0);
  const grandPaid     = doctorRevenues.reduce((sum, d) => sum + (d.net?.amountCollected || 0), 0);
  const grandPending  = doctorRevenues.reduce((sum, d) => sum + (d.net?.pendingAmount   || 0), 0);
  // For "today" we prefer the Firestore walk-in count (closer to live UX);
  // for other periods we use BE billing count.
  const grandBillings = activeFilter === 'today' && fsTotalVisits !== null
    ? fsTotalVisits
    : doctorRevenues.reduce((sum, d) => sum + (d.net?.billingCount    || 0), 0);
  const isTodayView = activeFilter === 'today';
  const periodLabel = formatPeriodLabel(dateRange, activeFilter);
  const clinicName = selectedClinic?.name || 'Clinic';

  const buildCurrentReportHtml = () => buildClinicRevenueReportHtml({
    clinicName,
    periodLabel,
    generatedAt: dayjs().format('YYYY-MM-DD HH:mm'),
    rows: patientRevenueRows,
    grandPaid,
    grandBillings: patientRevenueRows.reduce((sum, row) => sum + row.visits, 0) || grandBillings,
  });

  const handlePrintReport = () => {
    printHtml(buildCurrentReportHtml());
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const html2pdfModule = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default;
      const start = dateRange[0]?.format('YYYY-MM-DD') || 'start';
      const end = dateRange[1]?.format('YYYY-MM-DD') || 'end';
      await html2pdf()
        .set({
          margin: 0,
          filename: `clinic-revenue-report-${start}-to-${end}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(buildCurrentReportHtml())
        .save();
      message.success('Revenue PDF downloaded');
    } catch (err) {
      console.error('Failed to download revenue PDF:', err);
      message.error('Failed to download revenue PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Layout className="clinic-revenue-layout">
      {!isMobile && (
        <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      )}
      {isMobile && (
        <Drawer
          className="clinic-revenue-mobile-nav"
          placement="right"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          width={260}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          <DashboardSidebar collapsed={false} setCollapsed={() => {}} />
        </Drawer>
      )}
      <Layout className="clinic-revenue-main-layout">
        <DashboardHeader
          onSettingsClick={() => navigate('/settings')}
          onMenuClick={() => setMobileNavOpen(true)}
          isMobile={isMobile}
        />
        <Content className="clinic-revenue-content">
          {/* Header */}
          <Row className="clinic-revenue-page-header" justify="space-between" align="middle">
            <Col className="clinic-revenue-heading-column">
              <Space className="clinic-revenue-heading" align="center">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/dashboard')}
                  type="text"
                />
                <BarChartOutlined style={{ fontSize: 22, color: '#1890ff' }} />
                <Title className="clinic-revenue-title" level={3}>Clinic Revenue Overview</Title>
              </Space>
              <Text className="clinic-revenue-description" type="secondary">
                Total revenue across all doctors in your clinic
              </Text>
            </Col>
            <Col className="clinic-revenue-actions-column">
              <Space className="clinic-revenue-page-actions" wrap>
                <Button icon={<PrinterOutlined />} onClick={handlePrintReport} disabled={loading}>
                  Print Report
                </Button>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={handleDownloadPdf}
                  loading={pdfLoading}
                  disabled={loading}
                >
                  Download PDF
                </Button>
                <Button icon={<ReloadOutlined />} onClick={fetchRevenueData} loading={loading}>
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Date filter */}
          <Card className="clinic-revenue-filter-card" size="small">
            <Row className="clinic-revenue-filter-row" gutter={[12, 8]} align="middle" wrap>
              <Col className="clinic-revenue-period-label">
                <Text strong>Period: </Text>
              </Col>
              <Col className="clinic-revenue-shortcuts-column">
                <Space className="clinic-revenue-shortcuts" wrap>
                  {['today', 'week', 'month', 'year'].map(f => (
                    <Button
                      key={f}
                      size="small"
                      type={activeFilter === f ? 'primary' : 'default'}
                      onClick={() => applyFilter(f)}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </Button>
                  ))}
                </Space>
              </Col>
              <Col className="clinic-revenue-range-column">
                <RangePicker
                  className="clinic-revenue-range-picker"
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates) { setActiveFilter('custom'); setDateRange([dates[0], dates[1]]); }
                  }}
                  size="small"
                />
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            {/* Grand Total Card */}
            <Card
              style={{
                marginBottom: 24,
                background: isTodayView
                  ? 'linear-gradient(135deg, #fa541c 0%, #d4380d 100%)'
                  : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                borderRadius: 12,
              }}
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic
                    title={
                      <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                        {isTodayView
                          ? <><FireOutlined /> الصافي اليوم</>
                          : 'Total Clinic Revenue'}
                      </Text>
                    }
                    value={grandTotal}
                    precision={0}
                    suffix="EGP"
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: 'white', fontSize: 28, fontWeight: 700 }}
                    formatter={(val) => <CountUp end={Number(val)} />}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Pending</Text>}
                    value={grandPending}
                    precision={0}
                    suffix="EGP"
                    valueStyle={{ color: '#ffd666', fontSize: 22 }}
                    formatter={(val) => <CountUp end={Number(val)} />}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title={
                      <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                        {isTodayView ? <><FireOutlined /> Visits Today</> : 'Total Visits Billed'}
                      </Text>
                    }
                    value={grandBillings}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: 'white', fontSize: 22 }}
                    formatter={(val) => <CountUp end={Number(val)} />}
                  />
                </Col>
              </Row>
            </Card>

            {/* Per-doctor cards */}
            {doctorRevenues.length === 0 && !loading && (
              <Empty description="No revenue data found for this period." />
            )}

            <Row gutter={[16, 16]}>
              {doctorRevenues.map((dr, idx) => {
                const color = DOCTOR_COLORS[idx % DOCTOR_COLORS.length];
                const share = grandTotal > 0 ? Math.round((dr.net?.totalRevenue || 0) / grandTotal * 100) : 0;
                const hasData = !!dr.net && dr.net.billingCount > 0;
                return (
                  <Col key={dr.doctorId} xs={24} sm={12} lg={8}>
                    <Card
                      style={{ borderTop: `4px solid ${color}`, borderRadius: 10 }}
                      hoverable
                    >
                      <Space style={{ marginBottom: 12 }}>
                        <Avatar style={{ backgroundColor: color }} icon={<UserOutlined />} />
                        <div>
                          <Text strong style={{ fontSize: 15 }}>{dr.doctorName}</Text>
                          {dr.error && <Tag color="error" style={{ marginLeft: 6 }}>No data</Tag>}
                        </div>
                      </Space>

                      {hasData && dr.net ? (
                        // ── Always BE — single source of truth ────────────
                        <>
                          <Statistic
                            value={dr.net.totalRevenue}
                            suffix="EGP"
                            prefix={<DollarOutlined />}
                            valueStyle={{ color, fontSize: 24, fontWeight: 700 }}
                            formatter={(val) => <CountUp end={Number(val)} />}
                          />
                          <div style={{ marginTop: 8, marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Share of clinic total
                            </Text>
                          </div>
                          <Progress
                            percent={share}
                            strokeColor={color}
                            trailColor="#f0f0f0"
                            size="small"
                          />
                          <Divider style={{ margin: '12px 0' }} />
                          <Row gutter={8}>
                            <Col span={24}>
                              <Statistic
                                title={<Text style={{ fontSize: 11 }}>Visits</Text>}
                                value={dr.net.billingCount}
                                valueStyle={{ fontSize: 14 }}
                              />
                            </Col>
                          </Row>
                          {dr.net.pendingAmount > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="warning">
                                Pending: {dr.net.pendingAmount.toLocaleString()} EGP
                              </Tag>
                            </div>
                          )}
                          {dr.net.refundedAmount > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="error">
                                Refunded: {dr.net.refundedAmount.toLocaleString()} EGP
                              </Tag>
                            </div>
                          )}
                        </>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={dr.error ? 'Could not load data' : 'No billing records'}
                          style={{ margin: '12px 0' }}
                        />
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Revenue share summary */}
            {doctorRevenues.length > 1 && grandTotal > 0 && (
              <>
                <Divider style={{ marginTop: 32 }}>Revenue Distribution</Divider>
                <Card>
                  {doctorRevenues
                    .slice()
                    .sort((a, b) => (b.net?.totalRevenue || 0) - (a.net?.totalRevenue || 0))
                    .map((dr, idx) => {
                      const color = DOCTOR_COLORS[doctorRevenues.indexOf(dr) % DOCTOR_COLORS.length];
                      const share = grandTotal > 0
                        ? ((dr.net?.totalRevenue || 0) / grandTotal * 100).toFixed(1)
                        : '0';
                      return (
                        <div key={dr.doctorId} style={{ marginBottom: 12 }}>
                          <Row className="clinic-revenue-distribution-row" justify="space-between">
                            <Col className="clinic-revenue-distribution-name">
                              <Space>
                                <span style={{ color, fontWeight: 600 }}>#{idx + 1}</span>
                                <Text>{dr.doctorName}</Text>
                              </Space>
                            </Col>
                            <Col className="clinic-revenue-distribution-total">
                              <Text strong>{(dr.net?.totalRevenue || 0).toLocaleString()} EGP</Text>
                              <Text type="secondary" style={{ marginLeft: 8 }}>({share}%)</Text>
                            </Col>
                          </Row>
                          <Progress
                            percent={Number(share)}
                            strokeColor={color}
                            showInfo={false}
                            size="small"
                          />
                        </div>
                      );
                    })}
                </Card>
              </>
            )}
          </Spin>
        </Content>
      </Layout>
    </Layout>
  );
};

export default ClinicRevenueOverview;
